@import "button-theme";

/**
@prop --ux-button-min-width: min-width of the default button
@prop --ux-button-min-height: min-height of the default button
@prop --ux-button-padding-horizontal: left and right padding
@prop --ux-button-padding-vertical: top and bottom padding
@prop --ux-button-bg-color: default background color
@prop --ux-button-border-color: default border color
@prop --ux-button-text-color: default text color
@prop --ux-button-shadow: default box-shadow style
@prop --ux-button-icon-size: size of the icon
@prop --ux-button-icon-size-large: icon size for icon-only buttons
@prop --ux-button-outline-width: button outline-width
@prop --ux-button-outline-offset: offset of the outline
@prop --ux-button-hover-bg-color: background color in hover state
@prop --ux-button-hover-border-color: border color in hover state
@prop --ux-button-hover-text-color: text color in hover state
@prop --ux-button-active-bg-color: background color in active state
@prop --ux-button-active-border-color: border color in active state
@prop --ux-button-active-text-color: text color in active state
@prop --ux-button-focus-bg-color: background color if the button has focus
@prop --ux-button-focus-border-color: border color if the button has focus
@prop --ux-button-focus-text-color: text color if the button has focus
@prop --ux-button-focus-shadow: box-shadow if the button has focus
@prop --ux-icon-button-width: width of the icon-only button
@prop --ux-icon-button-height: height of the icon-only button
@prop --ux-icon-button-height: height of the icon-only button
*/

:host {
  box-sizing: border-box;
  display: inline-block;
  max-width: 100%;
  vertical-align: middle;
  --button-border-left-width: var(--ux-button-border-width);
  --button-border-right-width: var(--ux-button-border-width);
}

button,
a {
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: var(--ux-font);
  font-weight: var(--ux-button-font-weight);
  font-size: var(--ux-button-font-size);
  color: var(--ux-button-text-color);
  line-height: 1.5em;
  background-color: var(--ux-button-bg-color);
  border: solid var(--ux-button-border-color);
  border-top-width: var(--ux-button-border-width);
  border-bottom-width: var(--ux-button-border-width);
  border-left-width: var(--button-border-left-width);
  border-right-width: var(--button-border-right-width);
  border-radius: 0;
  box-shadow: var(--ux-button-shadow);
  box-sizing: border-box;
  outline: var(--ux-button-outline-width) solid transparent;
  outline-offset: var(--ux-button-outline-offset);
  text-align: center;
  text-decoration: none;
  text-transform: var(--ux-button-text-transform);
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  width: 100%;
  cursor: pointer;
  transition: var(--ux-button-transition);

  @container style(--trumpf-ux-animations: disabled) {
    transition-delay: 0s;
  }

  min-height: var(--ux-button-min-height);
  min-width: var(--ux-button-min-width);
  padding: 0 var(--ux-button-padding-horizontal);
  position: relative;

  @include button-interactive(false);

  &::before {
    @include absolute();
    background: transparent;
    content: "";
  }
}

a {
  width: auto;
}

ux-icon {
  display: inline;
  font-size: var(--ux-button-icon-size);
  height: var(--ux-button-icon-size);
  color: var(--ux-button-icon-color, currentColor);
  fill: currentColor;
  transform: translateY(-1px);
}

:host([variant="primary"]) {

  button,
  a {
    background-color: var(--ux-button-primary-bg-color);
    border-color: var(--ux-button-primary-border-color);
    color: var(--ux-button-primary-text-color);
    @include button-interactive(primary);

    &:not(:disabled):active::before {
      background-color: var(--ux-button-primary-active-bg-alt-color);
    }

    &:disabled {
      background-color: var(--ux-button-primary-disabled-bg-color);
      border-color: var(--ux-button-primary-disabled-border-color);
      color: var(--ux-button-primary-disabled-text-color);
    }
  }
}

:host([variant="no-border"]) {
  --ux-button-shadow: none;

  button,
  a {
    border: 0;
    padding-left: 0;
    padding-right: 0;

    &:active {
      color: var(--ux-button-text-color);
      background-color: var(--ux-button-bg-color);
    }

    &:not(:active, .ux-button--disabled):hover {
      background-color: var(--ux-button-bg-color);
      color: var(--ux-button-text-color);

      ux-icon {
        fill: var(--ux-button-icon-color, $color-green-100);
      }
    }
  }
}

:host([disabled]:not([disabled="false"])) {
  pointer-events: none;

  button,
  a {
    font-weight: var(--ux-button-disabled-font-weight);
    opacity: var(--ux-button-disabled-opacity);
    box-shadow: none;
  }
}

:host([selected]:not([selected="false"])) {

  button,
  button:disabled {
    background: var(--ux-button-selected-bg-color);
    border-color: var(--ux-button-selected-border-color);
    box-shadow: var(--ux-button-selected-shadow);
    color: var(--ux-button-selected-text-color);
    opacity: 1;
  }

  &:host([variant="primary"]) {

    button,
    button:disabled {
      background: var(--ux-button-primary-selected-bg-color);
      border-color: var(--ux-button-primary-selected-border-color);
      color: var(--ux-button-primary-selected-text-color);
      opacity: 1;
    }
  }
}

:host([size="icon"]) {

  &:host([iconplacement="start"]),
  &:host([iconplacement="end"]) {
    .ux-button__icon {
      margin-left: 0;
      margin-right: 0;
    }
  }

  button,
  a {
    min-width: var(--ux-icon-button-width);
    min-height: var(--ux-icon-button-height);
    width: var(--ux-icon-button-width);
    height: var(--ux-icon-button-height);
    margin: 0;
    padding: 0;

    ux-icon {
      height: var(--ux-button-icon-size-large);
      font-size: var(--ux-button-icon-size-large);
      margin-left: 0;
      margin-right: 0;
      top: 0;
      transform: none;
    }
  }
}

:host([iconPlacement="start"]) {

  button,
  a {
    .ux-button__icon {
      margin-left: calc(var(--ux-button-icon-spacing) * -1);
      margin-right: var(--ux-button-icon-spacing);
    }
  }

  &:host([variant="no-border"]) {

    button,
    a {
      .ux-button__icon {
        margin-left: 0;
      }
    }
  }
}

:host([iconPlacement="end"]) {

  button,
  a {
    flex-direction: row-reverse;

    .ux-button__icon {
      margin-left: var(--ux-button-icon-spacing);
      margin-right: calc(var(--ux-button-icon-spacing) * -1);
    }
  }

  &:host([variant="no-border"]) {

    button,
    a {
      .ux-button__icon {
        margin-right: 0;
      }
    }
  }
}

.ux-button__label {
  padding: var(--ux-button-padding-vertical) 0;

  &--hidden {
    display: none;
  }

  &--truncate {
    @include truncate();
  }
}

.ux-button__icon {
  &--custom {
    height: var(--ux-icon-height);
    width: var(--ux-icon-width);

    svg {
      height: 100%;
      width: 100%;
    }
  }

  &--none {
    display: none;
  }
}

.ux-button__label-visibility {
  &--s {
    @include button-label-visibility($bp-s, "default");
  }

  &--m {
    @include button-label-visibility($bp-m, "default");
  }

  &--l {
    @include button-label-visibility($bp-l, "default");
  }

  &--xl {
    @include button-label-visibility($bp-xl, "default");
  }
}